import { useCallback, useEffect, useMemo } from 'react';
import { useSurah, useSurahList } from '@/hooks/useSurah';
import { useAyahsBySurah } from '@/hooks/useAyah';
import { useSettings } from '@/hooks/useSettings';
import { useAyahAudio } from '@/hooks/useAyahAudio';
import {
  getGlobalAyahNumber,
  getMisharyAudioUrl,
} from '@/lib/utils/quranAyahNumbering';
import type {
  MemorizationMode,
  MemorizationSessionState,
  RepeatMode,
} from '@/types/memorization';
import { AyahDisplay } from './AyahDisplay';
import { AudioControls } from './AudioControls';
import { ModeToggle } from './ModeToggle';
import { SessionProgress } from './SessionProgress';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils/cn';

/**
 * Active memorization session view.
 *
 * Orchestrates:
 *   - data fetching (surah, ayahs, surah list for global-ayah calc)
 *   - audio playback (useAyahAudio with Mishary Alafasy URL)
 *   - keyboard shortcuts (← → for nav, space/k for play-pause,
 *     r for reset, h for hide-toggle), with input-guard
 *   - mode + repeat preferences (persisted via parent session hook)
 *   - mark-as-memorized + prev/next + end-session actions
 *
 * Pure orchestrator — the actual rendering work is in the small
 * components imported above.
 */

type MemorizationViewProps = {
  session: MemorizationSessionState;
  onNextAyah: () => void;
  onPrevAyah: () => void;
  onMarkMemorized: (ayahNumber: number) => void;
  onUnmarkMemorized: (ayahNumber: number) => void;
  onModeChange: (mode: MemorizationMode) => void;
  onRepeatModeChange: (mode: RepeatMode) => void;
  onEndSession: () => void;
};

export function MemorizationView({
  session,
  onNextAyah,
  onPrevAyah,
  onMarkMemorized,
  onUnmarkMemorized,
  onModeChange,
  onRepeatModeChange,
  onEndSession,
}: MemorizationViewProps) {
  const surahQuery = useSurah(session.surahId);
  const ayahsQuery = useAyahsBySurah(session.surahId);
  const surahListQuery = useSurahList();
  const { settings } = useSettings();

  // Current ayah from the loaded array, by ayah_number.
  const currentAyah = useMemo(
    () =>
      ayahsQuery.data?.find((a) => a.ayah_number === session.currentAyah) ??
      null,
    [ayahsQuery.data, session.currentAyah],
  );

  // Compute global ayah number for audio. Falls back to surah-relative
  // when the surah list is still loading; useAyahAudio will simply not
  // start playing in that window.
  const globalAyahNumber = useMemo(() => {
    if (!surahListQuery.data) return null;
    return getGlobalAyahNumber(
      session.surahId,
      session.currentAyah,
      surahListQuery.data,
    );
  }, [surahListQuery.data, session.surahId, session.currentAyah]);

  const audioUrl =
    globalAyahNumber !== null ? getMisharyAudioUrl(globalAyahNumber) : null;

  const audio = useAyahAudio({
    audioUrl,
    initialRepeatMode: session.repeatMode,
  });

  // Sync the audio hook's repeat mode with persisted session state.
  // (User can change repeat mid-session; we want it to survive reload.)
  useEffect(() => {
    if (audio.repeatMode !== session.repeatMode) {
      audio.setRepeatMode(session.repeatMode);
    }
    // Only respond to session.repeatMode changes — not audio.repeatMode,
    // which would create a feedback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.repeatMode]);

  const handleRepeatModeChange = useCallback(
    (mode: RepeatMode) => {
      audio.setRepeatMode(mode);
      onRepeatModeChange(mode);
    },
    [audio, onRepeatModeChange],
  );

  // ---- Memorize / unmark toggle for current ayah ----
  const isCurrentMemorized = session.completedAyahs.includes(
    session.currentAyah,
  );
  const toggleCurrentMemorized = useCallback(() => {
    if (isCurrentMemorized) onUnmarkMemorized(session.currentAyah);
    else onMarkMemorized(session.currentAyah);
  }, [
    isCurrentMemorized,
    onMarkMemorized,
    onUnmarkMemorized,
    session.currentAyah,
  ]);

  // ---- Hide/reveal shortcut → cycle mode ----
  const toggleHide = useCallback(() => {
    if (session.mode === 'hide' || session.mode === 'recall') {
      onModeChange('read');
    } else {
      onModeChange('hide');
    }
  }, [session.mode, onModeChange]);

  // ---- Boundary flags ----
  const isAtStart = session.currentAyah <= session.startAyah;
  const isAtEnd = session.currentAyah >= session.endAyah;

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'SELECT' ||
        tag === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (!isAtEnd) onNextAyah();
        else onNextAyah(); // allow stepping past to trigger Complete
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!isAtStart) onPrevAyah();
      } else if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        if (audio.isPlaying) audio.pause();
        else audio.play();
      } else if (e.key === 'r') {
        e.preventDefault();
        audio.stop();
      } else if (e.key === 'h') {
        e.preventDefault();
        toggleHide();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    audio,
    isAtEnd,
    isAtStart,
    onNextAyah,
    onPrevAyah,
    toggleHide,
  ]);

  // ---- Error state ----
  if (surahQuery.isError || ayahsQuery.isError) {
    const err = (surahQuery.error ?? ayahsQuery.error) as Error | null;
    return (
      <EmptyState
        tone="error"
        title="We couldn't load this surah."
        description={err?.message ?? 'Try again.'}
        onRetry={() => {
          surahQuery.refetch();
          ayahsQuery.refetch();
        }}
      />
    );
  }

  const isLoading = surahQuery.isLoading || ayahsQuery.isLoading;

  const totalInRange = session.endAyah - session.startAyah + 1;

  return (
    <section className="container-page pt-10 sm:pt-14 pb-16">
      {/* Header */}
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-2">
            Memorize · Session
          </p>
          {surahQuery.data ? (
            <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
              {surahQuery.data.name_en}
              <span className="text-ink-400 dark:text-ink-500 text-xl ml-3 align-middle">
                · Ayah {session.currentAyah}
              </span>
            </h1>
          ) : (
            <Skeleton className="h-12 w-64" />
          )}
        </div>
        <Button variant="ghost" onClick={onEndSession} aria-label="End session">
          End session
        </Button>
      </header>

      {/* Progress bar */}
      <div className="mb-6">
        <SessionProgress
          memorizedCount={session.completedAyahs.length}
          totalCount={totalInRange}
          currentAyah={session.currentAyah}
          startAyah={session.startAyah}
          endAyah={session.endAyah}
        />
      </div>

      {/* Mode toggle */}
      <div className="mb-5">
        <ModeToggle value={session.mode} onChange={onModeChange} />
      </div>

      {/* Ayah display */}
      <div className="mb-5">
        {isLoading || !currentAyah ? (
          <AyahSkeleton />
        ) : (
          <AyahDisplay
            ayah={currentAyah}
            mode={session.mode}
            translationLanguage={settings.translation_language}
            showTransliteration={settings.show_transliteration}
            isMemorized={isCurrentMemorized}
          />
        )}
      </div>

      {/* Audio controls */}
      <div className="mb-6">
        <AudioControls
          isPlaying={audio.isPlaying}
          isLoading={audio.isLoading}
          error={audio.error}
          repeatMode={audio.repeatMode}
          onPlay={audio.play}
          onPause={audio.pause}
          onStop={audio.stop}
          onRetry={audio.retry}
          onRepeatModeChange={handleRepeatModeChange}
          prominent={session.mode === 'repeat'}
        />
      </div>

      {/* Actions */}
      <nav
        aria-label="Session actions"
        className={cn(
          'grid grid-cols-2 sm:grid-cols-3 gap-3',
          'pt-6 border-t border-ink-200/60 dark:border-ink-800/60',
        )}
      >
        <Button
          variant="secondary"
          onClick={onPrevAyah}
          disabled={isAtStart || isLoading}
          aria-label="Previous ayah"
        >
          ← Previous
        </Button>
        <Button
          variant={isCurrentMemorized ? 'ghost' : 'primary'}
          onClick={toggleCurrentMemorized}
          disabled={isLoading || !currentAyah}
          className="col-span-2 sm:col-span-1"
          aria-label={
            isCurrentMemorized ? 'Unmark as memorized' : 'Mark as memorized'
          }
        >
          {isCurrentMemorized ? '✓ Memorized — undo' : 'Mark as memorized'}
        </Button>
        <Button
          variant="primary"
          onClick={onNextAyah}
          disabled={isLoading}
          aria-label="Next ayah"
        >
          {isAtEnd ? 'Finish →' : 'Next →'}
        </Button>
      </nav>

      <p className="mt-6 text-xs text-ink-400 dark:text-ink-500 text-center">
        Shortcuts: ← / → navigate · space or K play/pause · R reset · H hide
      </p>
    </section>
  );
}

function AyahSkeleton() {
  return (
    <div className="py-10 px-5 sm:px-8 rounded-2xl border border-ink-200/60 dark:border-ink-800/60">
      <Skeleton className="h-4 w-20 mb-6" />
      <Skeleton className="h-10 w-full mb-3" />
      <Skeleton className="h-10 w-5/6 mb-8" />
      <Skeleton className="h-5 w-full mb-2" />
      <Skeleton className="h-5 w-4/5" />
    </div>
  );
}
